import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { Routes } from "@/config/page-view-constants";
import axioInstance from "@/libs/axios";
import { RootState } from "@/store/root-reducer";
import { QuoteData } from "@/types/quote/quote-data";
import { PayloadType, QuoteSubmitPayloadType } from "@/types/QuoteSubmitTypes";
import logError from "@/utils/logger";

// Custom hook for fetching data
export default function useQuoteSubmit(resetTrigger: () => void) {
  const [responseCode, setResponseCode] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | AxiosError>(null);
  const [newQuote, setNewQuote] = useState<QuoteData>();

  const { attributes, version, referenceId } = useSelector(
    (state: RootState) => state.quote
  );

  const quoteSubmit = useCallback(
    async (payloadData: PayloadType) => {
      const payLoad: QuoteSubmitPayloadType = {
        data: {
          type: "Quote",
          channel: "ONLINE",
          version,
          viewPage: Routes.Quote,
          attributes: {
            domain: attributes.domain,
            paymentOption: payloadData.paymentOption,
            experimentDetails: payloadData.experimentDetails ?? undefined,
          },
        },
      };

      try {
        const response = await axioInstance.patch(
          `${process.env.NEXT_PUBLIC_BASE_URL}/quote/${referenceId}/quotePageSubmit`,
          payLoad
        );
        setResponseCode(response.data.code);
        setNewQuote(response.data.data);
      } catch (err) {
        const axiosError = err as AxiosError;
        logError(axiosError, { location: "Quote Page Submit" });
        setError(axiosError);
      } finally {
        setLoading(false);
        resetTrigger();
      }
    },
    [attributes.domain, version, referenceId, resetTrigger]
  );

  return { responseCode, loading, error, quoteSubmit, newQuote };
}
